import {
  Col,
  Row,
  Text,
  CMSLayout,
  useUIState,
  Button,
  Table,
  modal,
  Select01,
  TouchField,
  useRequireAuth,
  LoginForm,
} from "components";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { IScreen } from "type";
import Store from "store";
import { useNavFunc } from "navigation";
import { SCREEN, COLOR, COLORS } from "const";
import { ActivityIndicator } from "react-native";
import moment from "moment";
import { TimeHelper, calculateTimeOpen } from "helpers";
import { Select, Button as AntButton } from "antd";
import ChatMessagesModal from "./ChatMessagesModal";

interface OrderIssue {
  id: string;
  orderId: string;
  userId: string;
  type: 'missing' | 'wrong-item' | 'damaged' | 'other';
  description: string;
  status: 'pending' | 'in-progress' | 'resolved' | 'rejected';
  attachFiles?: string[];
  createdAt?: string;
  updatedAt?: string;
  order?: { sorId?: string };
  company?: { companyName?: string; externalId?: string };
  location?: { name?: string; externalId?: string };
  raisedBy?: { firstName?: string; lastName?: string; email?: string };
}

const ListOrderIssues: IScreen = () => {
  const { isLoggedIn } = useRequireAuth();
  const { navigation } = useNavFunc();
  const [{ fetching, errorMes }, setUI] = useUIState({
    fetching: false,
  });
  const [issues, setIssues] = useState<OrderIssue[]>([]);
  const [pagination, setPagination] = useState({ hasNext: false, total: 0 });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const tokenProcessedRef = useRef<boolean>(false);
  const [hasTokenInUrl, setHasTokenInUrl] = useState(false);
  const [tokenCheckDone, setTokenCheckDone] = useState(false);
  const UserStore = Store.useUserStore();

  const noSidebar = useMemo(() => {
    if (typeof window === "undefined") return false;
    const urlParams = new URLSearchParams(window.location.search);
    const v = urlParams.get("noSidebar");
    return v === "1" || v === "true" || v === "yes";
  }, []);

  const noHeader = useMemo(() => {
    if (typeof window === "undefined") return false;
    const urlParams = new URLSearchParams(window.location.search);
    const v = urlParams.get("noHeader");
    return v === "1" || v === "true" || v === "yes";
  }, []);

  useEffect(() => {
    const handleTokenFromUrl = async () => {
      if (typeof window === "undefined") return;
      if (tokenProcessedRef.current) return;

      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (token) {
        setHasTokenInUrl(true);
        tokenProcessedRef.current = true;
        try {
          let isSuccess = false;
          const res = await Store.Client.Api.User.validateShareToken({ token });
          if (res.data.success && res.data.data) {
            const tokenData = res.data.data;
            if (tokenData.access_token) {
              Store.Client.setToken(tokenData.access_token);
              UserStore.set({
                token: tokenData.access_token,
                user: tokenData.publicInfo || tokenData.user || tokenData,
              });
              isSuccess = true;
            }
          }

          if (!isSuccess) {
            Store.Client.setToken(token);
            const userRes = await Store.Client.Api.User.me();
            if (userRes.data.success && userRes.data.data) {
              UserStore.set({
                token,
                user: userRes.data.data,
              });
            }
          }
        } catch (err) {
          console.error("Error validating token:", err);
          tokenProcessedRef.current = false;
        } finally {
          setHasTokenInUrl(false);
          setTokenCheckDone(true);
        }
      } else {
        setTokenCheckDone(true);
      }
    };

    handleTokenFromUrl();
  }, []);

  const getData = async (p = 1, reset = false) => {
    try {
      if (reset) {
        setIssues([]);
        setPage(1);
      }
      setUI({ fetching: true, errorMes: "" });

      const query: any = {
        limit: 50,
        offset: (p - 1) * 50,
        orderBy: 'createdAt',
        details: true,
      };

      if (statusFilter) {
        query.status = statusFilter;
      }

      if (typeFilter) {
        query.type = typeFilter;
      }

      const res = await Store.Api.Shopify.listOrderIssues(query);

      if (res.data.success) {
        const newIssues = res.data.data.issues || [];
        const total = res.data.data.total || 0;
        const currentOffset = (p - 1) * 50;
        const hasNext = (currentOffset + newIssues.length) < total;

        setIssues(reset ? newIssues : [...issues, ...newIssues]);
        setPagination({
          hasNext: hasNext,
          total: total,
        });
        setPage(p);
      } else {
        setUI({ fetching: false, errorMes: res.data.error || "Failed to load order issues" });
        return;
      }

      setUI({ fetching: false, errorMes: "" });
    } catch (err) {
      setUI({ fetching: false, errorMes: String(err) });
    }
  };

  useEffect(() => {
    if (!tokenCheckDone) return;
    const unsubscribe = navigation.addListener("focus", () => {
      getData(1, true);
    });
    getData(1, true);
    return unsubscribe;
  }, [tokenCheckDone, statusFilter, typeFilter]);

  const handleViewChat = (issue: OrderIssue) => {
    modal.show(
      <ChatMessagesModal
        issueId={issue.id}
        issue={issue}
        onStatusUpdate={() => {
          getData(1, true);
        }}
      />
    );
  };

  const handleRowClick = (issue: OrderIssue) => {
    navigation.navigate(SCREEN.OrderIssueDetail, {
      issueId: issue.id,
      noSidebar,
      noHeader,
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (text: string) => (
        <Text numberOfLines={1} style={{ maxWidth: 200, fontFamily: 'monospace', fontSize: 11 }}>
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 140,
      render: (text: string, record: OrderIssue) => (
        <Col>
          <Text numberOfLines={1} style={{ maxWidth: 140, fontFamily: 'monospace', fontSize: 11 }}>
            {text?.substring(0, 12)}...
          </Text>
          {record.order?.sorId ? (
            <Text numberOfLines={1} fontSize={10} color={COLOR.GREY} style={{ maxWidth: 140 }}>
              {record.order.sorId}
            </Text>
          ) : null}
        </Col>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 180,
      render: (_: any, record: OrderIssue) => (
        <Col>
          <Text numberOfLines={1} style={{ maxWidth: 180 }}>
            {record.company?.companyName || '-'}
          </Text>
          {record.company?.externalId ? (
            <Text numberOfLines={1} fontSize={10} color={COLOR.GREY} style={{ maxWidth: 180 }}>
              {record.company.externalId}
            </Text>
          ) : null}
        </Col>
      ),
    },
    {
      title: 'Site',
      key: 'site',
      width: 180,
      render: (_: any, record: OrderIssue) => (
        <Col>
          <Text numberOfLines={1} style={{ maxWidth: 180 }}>
            {record.location?.name || '-'}
          </Text>
          {record.location?.externalId ? (
            <Text numberOfLines={1} fontSize={10} color={COLOR.GREY} style={{ maxWidth: 180 }}>
              {record.location.externalId}
            </Text>
          ) : null}
        </Col>
      ),
    },
    {
      title: 'Raised By',
      key: 'raisedBy',
      width: 220,
      render: (_: any, record: OrderIssue) => {
        const fullName = `${record.raisedBy?.firstName || ''} ${record.raisedBy?.lastName || ''}`.trim();
        return (
          <Col>
            <Text numberOfLines={1} style={{ maxWidth: 220 }}>
              {fullName || record.raisedBy?.email || '-'}
            </Text>
            {record.raisedBy?.email && fullName ? (
              <Text numberOfLines={1} fontSize={10} color={COLOR.GREY} style={{ maxWidth: 220 }}>
                {record.raisedBy.email}
              </Text>
            ) : null}
          </Col>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Text style={{ textTransform: 'capitalize' }}>
          {type?.replace('-', ' ')}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusColors: { [key: string]: string } = {
          pending: '#faad14',
          'in-progress': '#1890ff',
          resolved: '#52c41a',
          rejected: '#ff4d4f',
        };
        return (
          <Text style={{
            color: statusColors[status] || '#000',
            textTransform: 'capitalize',
            fontWeight: 'bold',
          }}>
            {status?.replace('-', ' ')}
          </Text>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (text: string) => (
        <Text numberOfLines={2} style={{ maxWidth: 300 }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => (
        <Text>{date ? TimeHelper.formatDay(date) : '-'}</Text>
      ),
    },
    {
      title: 'Time Open',
      key: 'timeOpen',
      width: 150,
      render: (_: any, record: OrderIssue) => {
        if (!record.createdAt) return <Text>-</Text>;
        const timeOpen = calculateTimeOpen(record.createdAt, record.updatedAt, record.status);
        const colorMap = {
          green: '#52c41a',
          amber: '#faad14',
          red: '#ff4d4f',
        };
        return (
          <Text style={{ 
            color: colorMap[timeOpen.color],
            fontWeight: timeOpen.isOverSLA ? 'bold' : 'normal',
          }}>
            {timeOpen.formatted}
            {timeOpen.isOverSLA && ' ⚠️'}
          </Text>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: OrderIssue) => (
        <AntButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleViewChat(record);
          }}
          style={{ backgroundColor: COLORS.GREEN }}
        >
          <Text fontSize={14} fontWeight="600" colorWhite>View Chat</Text>
        </AntButton>
      ),
    },
  ];

  if (!isLoggedIn && !hasTokenInUrl) {
    return (
      <LoginForm
        title="Login Required"
        description="Please login to access Order Issues"
      />
    );
  }

  return (
    <CMSLayout noSidebar={noSidebar} noHeader={noHeader}>
      <Col flex1 p2>
        <Row justifyContent="space-between" alignItems="center" mb2>
          <Text h2>Order Issues</Text>
        </Row>

        <Row mb2 gap={10}>
          <Col width={200}>
            <Text mb1>Status Filter:</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="All Statuses"
              allowClear
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
            >
              <Select.Option value="pending">Pending</Select.Option>
              {/* <Select.Option value="in-progress">In Progress</Select.Option> */}
              <Select.Option value="resolved">Resolved</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
          </Col>
          <Col width={200}>
            <Text mb1>Type Filter:</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="All Types"
              allowClear
              value={typeFilter}
              onChange={(value) => setTypeFilter(value)}
            >
              <Select.Option value="missing">Missing</Select.Option>
              <Select.Option value="wrong-item">Wrong Item</Select.Option>
              <Select.Option value="damaged">Damaged</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Col>
        </Row>

        {errorMes && (
          <Col mb2 p2 backgroundColor="#fff2f0" round1>
            <Text color="#ff4d4f">{errorMes}</Text>
          </Col>
        )}

        {fetching && issues.length === 0 ? (
          <Col flex1 middle>
            <ActivityIndicator size="large" />
            <Text mt2>Loading order issues...</Text>
          </Col>
        ) : issues.length === 0 ? (
          <Col flex1 middle>
            <Text>No order issues found</Text>
          </Col>
        ) : (
          <Col flex1>
            <Table
              data={issues}
              columns={columns}
              minWidth={1800}
              rowKey={(record) => record.id}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer' },
              })}
            />
            {pagination.hasNext && (
              <Row justifyContent="center" mt2>
                <Button
                  text="Load More"
                  onPress={() => getData(page + 1, false)}
                  isLoading={fetching}
                />
              </Row>
            )}
            <Row justifyContent="center" mt1>
              <Text color={COLOR.GREY}>
                Showing {issues.length} of {pagination.total} issues
              </Text>
            </Row>
          </Col>
        )}
      </Col>
    </CMSLayout>
  );
};

ListOrderIssues.routeInfo = {
  title: "Order Issues",
  path: "/order-issues",
};

export default ListOrderIssues;

